import { Package, Clock, Users, Home, CheckCircle, X } from 'lucide-react';

const TripPackages = ({ packages, selectedPackage, onPackageSelect, selectedStartDate, selectedEndDate, guestCount, pricingModel, dailyPrice, basePrice, minStay }) => {
  if (!packages || packages.length === 0) return null;

  // Handle package selection/deselection
  const handlePackageClick = (pkg, index) => {
    const isCurrentlySelected = selectedPackage && (selectedPackage.id === pkg.id || index === selectedPackage.index);
    if (isCurrentlySelected) {
      // Unselect if clicking the same package
      onPackageSelect(null, null);
    } else {
      // Select new package
      onPackageSelect(pkg, index);
    }
  };

  // Calculate nights from date range or package duration
  const calculateNights = () => {
    // If date range is selected, use actual nights
    if (selectedStartDate && selectedEndDate) {
      const diffTime = Math.abs(selectedEndDate - selectedStartDate);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    // If package is selected but no date range, use package duration
    if (selectedPackage) {
      return selectedPackage.durationType === 'fixed' 
        ? selectedPackage.durationNights 
        : selectedPackage.durationMin || 1;
    }
    return 0;
  };

  // Calculate total price for selected package
  const calculatePackagePrice = () => {
    if (!selectedPackage) return 0;
    const nights = calculateNights();
    // Package pricing always uses package.price, not dailyPrice
    return selectedPackage.price * guestCount;
  };

  const nights = calculateNights();
  const packagePrice = calculatePackagePrice();

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Package className="w-5 h-5 text-emerald-600" />
          <span>Select Package</span>
        </h2>
        {selectedPackage && (
          <button
            type="button"
            onClick={() => onPackageSelect(null, null)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear selection
          </button>
        )}
      </div>
      {!selectedPackage && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            {pricingModel === 'daily' && dailyPrice 
              ? `No package selected. Using daily pricing: $${dailyPrice} per person/night`
              : `No package selected. Using base pricing: $${basePrice} per person`}
          </p>
        </div>
      )}
      <div className="space-y-3">
        {packages.map((pkg, index) => {
          const isSelected = selectedPackage && (selectedPackage.id === pkg.id || index === selectedPackage.index);
          return (
            <button
              key={pkg.id || index}
              type="button"
              onClick={() => handlePackageClick(pkg, index)}
              className={`w-full text-left border-2 rounded-lg p-4 transition-all ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-emerald-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-gray-900">{pkg.name}</h3>
                    {isSelected && (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 mb-2">{pkg.description}</p>
                </div>
                <div className="text-right ml-3">
                  <div className="text-lg font-semibold text-emerald-700">${pkg.price}</div>
                  <div className="text-xs text-gray-600">per person</div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-emerald-600" />
                  <span>
                    {pkg.durationType === 'fixed' 
                      ? `${pkg.durationNights} ${pkg.durationNights === 1 ? 'night' : 'nights'}`
                      : `${pkg.durationMin}-${pkg.durationMax} nights`}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-emerald-600" />
                  <span>
                    {pkg.minGuests === pkg.maxGuests 
                      ? `${pkg.minGuests} ${pkg.minGuests === 1 ? 'guest' : 'guests'}`
                      : `${pkg.minGuests}-${pkg.maxGuests} guests`}
                  </span>
                </div>
              </div>
              
              {pkg.rooms && pkg.rooms.length > 0 && (
                <div className="pt-2 mt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Home className="w-3 h-3 text-emerald-600" />
                    <span className="text-xs font-medium text-gray-700">{pkg.rooms.length} accommodation option{pkg.rooms.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
      
      {selectedPackage && (
        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="text-xs text-gray-600 mb-1">Selected Package:</div>
              <div className="text-sm font-semibold text-emerald-900">{selectedPackage.name}</div>
            </div>
            <button
              type="button"
              onClick={() => onPackageSelect(null, null)}
              className="text-emerald-700 hover:text-emerald-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {nights > 0 && (
            <div className="pt-2 border-t border-emerald-200 mt-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600">
                  {nights} {nights === 1 ? 'night' : 'nights'} × {guestCount} {guestCount === 1 ? 'guest' : 'guests'}
                </span>
                <span className="text-gray-600">
                  ${selectedPackage.price} per person
                </span>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-emerald-200">
                <span className="text-sm font-medium text-gray-700">Total:</span>
                <span className="text-lg font-semibold text-emerald-900">
                  ${packagePrice.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripPackages;

